import React, { useState } from 'react';
import { Box, Typography, TextField, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../utils/formatCurrency';

// Which row field each language tab edits. EN is the required base name; es/ar
// are optional translations that fall back to the base when empty.
const NAME_FIELD = { en: 'size_name', es: 'size_name_es', ar: 'size_name_ar' };
const LANG_TABS = ['en', 'es', 'ar'];

/**
 * Edit a menu item's size variants — the "Small / Medium / Large" pill block
 * in the mockup. Selecting a pill marks it default; the inline "+ Add Size"
 * form appends a new variant. Each size carries an English base name plus
 * optional es / ar translations (mirrors addon options); the EN/ES/AR tab
 * chooses which name the per-pill + add-form name fields edit. Sizes are
 * persisted on parent submit (the parent sends the array as part of the PUT
 * payload, so no per-row API call here).
 */
const SizesEditor = ({ sizes, onChange, currency }) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNameEs, setNewNameEs] = useState('');
  const [newNameAr, setNewNameAr] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [activeNameTab, setActiveNameTab] = useState('en');

  const handleSelectDefault = (idx) => {
    onChange(sizes.map((s, i) => ({ ...s, is_default: i === idx })));
  };

  const handleUpdateName = (idx, field, value) => {
    onChange(sizes.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };

  const handleUpdatePrice = (idx, value) => {
    onChange(sizes.map((s, i) => (i === idx ? { ...s, price: value } : s)));
  };

  const handleRemove = (idx) => {
    const next = sizes.filter((_, i) => i !== idx);
    if (next.length > 0 && !next.some(s => s.is_default)) {
      next[0].is_default = true;
    }
    onChange(next);
  };

  const handleAdd = () => {
    const name = newName.trim();
    const priceNum = parseFloat(newPrice);
    if (!name || !Number.isFinite(priceNum) || priceNum < 0) return;
    onChange([
      ...sizes,
      {
        size_name: name,
        size_name_es: newNameEs.trim() || null,
        size_name_ar: newNameAr.trim() || null,
        price: priceNum,
        is_default: sizes.length === 0,
        display_order: sizes.length,
      },
    ]);
    setNewName('');
    setNewNameEs('');
    setNewNameAr('');
    setNewPrice('');
    setAdding(false);
  };

  // New-size name field is bound to the active language tab.
  const newNameValue = activeNameTab === 'es' ? newNameEs : activeNameTab === 'ar' ? newNameAr : newName;
  const setNewNameForTab = (value) => {
    if (activeNameTab === 'es') setNewNameEs(value);
    else if (activeNameTab === 'ar') setNewNameAr(value);
    else setNewName(value);
  };

  const namePlaceholder = activeNameTab === 'es'
    ? t('menuV2.edit.sizeNamePlaceholderEs', 'Name in Spanish (optional)')
    : activeNameTab === 'ar'
      ? t('menuV2.edit.sizeNamePlaceholderAr', 'Name in Arabic (optional)')
      : t('menuV2.edit.sizeNamePlaceholder', 'Name (e.g. Small)');

  return (
    <Box>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={activeNameTab}
        onChange={(_e, v) => { if (v) setActiveNameTab(v); }}
        aria-label={t('menuV2.edit.sizeNameLanguage', 'Size name language')}
        sx={{ mb: 1.5 }}
      >
        {LANG_TABS.map(code => (
          <ToggleButton key={code} value={code} sx={{ px: 1.5, py: 0.25, textTransform: 'uppercase' }}>
            {code}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {sizes.length > 0 ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(auto-fit, minmax(140px, 1fr))' },
            gap: 1,
          }}
        >
          {sizes.map((s, idx) => {
            const isDefault = !!s.is_default;
            const nameValue = s[NAME_FIELD[activeNameTab]] || '';
            return (
              <Box
                key={s.id ?? `new-${idx}`}
                onClick={() => handleSelectDefault(idx)}
                sx={{
                  position: 'relative',
                  cursor: 'pointer',
                  border: '1.5px solid',
                  borderColor: isDefault ? 'primary.main' : 'divider',
                  backgroundColor: isDefault ? 'action.selected' : 'background.paper',
                  borderRadius: 1.5,
                  paddingInline: 1.5,
                  paddingBlock: 1.25,
                  textAlign: 'center',
                  transition: 'border-color 120ms ease',
                  '&:hover': { borderColor: 'primary.main' },
                }}
              >
                {activeNameTab !== 'en' && (
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 0.25 }}>
                    {s.size_name}
                  </Typography>
                )}
                <TextField
                  size="small"
                  value={nameValue}
                  onChange={(e) => handleUpdateName(idx, NAME_FIELD[activeNameTab], e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder={namePlaceholder}
                  inputProps={{ maxLength: 60, 'aria-label': `${s.size_name} name`, style: { textAlign: 'center', fontWeight: 600, fontSize: 13 } }}
                  sx={{ width: '100%' }}
                />
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
                  {formatCurrency(s.price, currency)}
                </Typography>
                {isDefault && (
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{
                      position: 'absolute',
                      top: 4,
                      insetInlineEnd: 6,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: 'primary.main',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                    }}
                  >
                    ✓
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, mt: 1 }}>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: '0.01', 'aria-label': `${s.size_name} price` }}
                    value={s.price}
                    onChange={(e) => handleUpdatePrice(idx, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ width: '100%' }}
                  />
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleRemove(idx); }}
                  aria-label={t('common.remove', 'Remove')}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    insetInlineStart: 6,
                    width: 18,
                    height: 18,
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: 'text.secondary',
                    cursor: 'pointer',
                    fontSize: 14,
                    lineHeight: 1,
                    '&:hover': { color: 'error.main' },
                  }}
                >
                  ×
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
          {t('menuV2.edit.noSizes', 'No size variants — uses the base price.')}
        </Typography>
      )}

      {adding ? (
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder={namePlaceholder}
            value={newNameValue}
            onChange={(e) => setNewNameForTab(e.target.value)}
            inputProps={{ maxLength: 60 }}
            sx={{ flex: 1, minWidth: 140 }}
          />
          <TextField
            size="small"
            type="number"
            placeholder={t('menuV2.edit.pricePlaceholder', 'Price')}
            value={newPrice}
            onChange={(e) => setNewPrice(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
            sx={{ width: 110 }}
          />
          <Button size="small" variant="contained" onClick={handleAdd} disabled={!newName.trim()}>
            {t('common.add', 'Add')}
          </Button>
          <Button size="small" onClick={() => { setAdding(false); setNewName(''); setNewNameEs(''); setNewNameAr(''); setNewPrice(''); }}>
            {t('common.cancel', 'Cancel')}
          </Button>
        </Box>
      ) : (
        <Button size="small" onClick={() => setAdding(true)} sx={{ mt: 1 }}>
          + {t('menuV2.edit.addSize', 'Add Size')}
        </Button>
      )}
    </Box>
  );
};

export default SizesEditor;
